import moment from 'moment';
import React, { useEffect, useState } from "react";
import { Button, Table } from 'react-bootstrap';
import Alert from 'react-s-alert';
import ConfirmButton from '../../Shared/ConfirmButton';
import StandardFormModal from "../../Components/StandardFormModal/StandardFormModal";
import ApiKeysService from '../../utils/ApiKeysService';
import { formats } from '../../variables/variables';

// Admin page for managing API keys. The raw token is shown once (on
// create/rotate); after that only the em_live_xxxxxx prefix is visible.
const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [createModal, setCreateModal] = useState({ display: false, name: '', scope: 'read_only' });
  const [revealed, setRevealed] = useState(null); // { token, name } shown once

  const docsSecret = process.env.REACT_APP_DEVELOPERS_SECRET;

  const load = async () => {
    const response = await ApiKeysService.index();
    setKeys(response.results || []);
  };

  useEffect(() => { load(); }, []);

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    Alert.success("API key copied to clipboard!");
  };

  const changeScope = async (key, scope) => {
    if (scope === key.scope) return;
    try {
      await ApiKeysService.updateScope(key.id, scope);
      Alert.success(`"${key.name}" is now ${scope === 'read_only' ? 'read only' : 'read / write'}.`);
      load();
    } catch (e) {
      Alert.error("Could not update the key's access.");
    }
  };

  const createKey = async () => {
    const name = createModal.name.trim();
    if (!name) {
      Alert.error("Please give the key a name.");
      return;
    }
    try {
      const key = await ApiKeysService.create(name, createModal.scope);
      setCreateModal({ display: false, name: '', scope: 'read_only' });
      setRevealed({ token: key.token, name: key.name });
      load();
    } catch (e) {
      Alert.error("Could not create the API key.");
    }
  };

  const rotateKey = async (record) => {
    try {
      const key = await ApiKeysService.rotate(record.id);
      setRevealed({ token: key.token, name: key.name });
      load();
    } catch (e) {
      Alert.error("Could not rotate the API key.");
    }
  };

  const revokeKey = async (record) => {
    try {
      await ApiKeysService.revoke(record.id);
      Alert.success("API key revoked.");
      load();
    } catch (e) {
      Alert.error("Could not revoke the API key.");
    }
  };

  return (
    <div>
      <h1 className="first-title mb-0">API Keys</h1>
      <p className="text-muted">
        Keys for the Emerson Coaching API (used with Claude).{" "}
        {docsSecret && (
          <a href={`/developers/${docsSecret}`} target="_blank" rel="noopener noreferrer">
            View documentation &rarr;
          </a>
        )}
      </p>

      <div className="mb-2">
        <Button bsStyle="success" onClick={() => setCreateModal({ display: true, name: '', scope: 'read_only' })}>
          <i className="far fa-plus" /> Create API Key
        </Button>
      </div>

      <Table striped>
        <thead>
          <tr>
            <th>Name</th>
            <th>Access</th>
            <th>Key</th>
            <th>Last used</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {keys.length === 0 && (
            <tr><td colSpan={6} className="text-muted">No API keys yet.</td></tr>
          )}
          {keys.map((key) => (
            <tr key={key.id}>
              <td>
                {key.name}
                {key.created_by && <div><small className="text-muted">by {key.created_by}</small></div>}
              </td>
              <td>
                <select
                  className="form-control input-sm"
                  value={key.scope}
                  onChange={(e) => changeScope(key, e.target.value)}
                  style={{ width: 130, display: 'inline-block' }}
                >
                  <option value="read_write">Read / write</option>
                  <option value="read_only">Read only</option>
                </select>
              </td>
              <td><code>{key.token_prefix}&bull;&bull;&bull;&bull;</code></td>
              <td>{key.last_used_at ? moment(key.last_used_at).format(formats.fullDateTime) : <span className="text-muted">never</span>}</td>
              <td>{key.created_at ? moment(key.created_at).format(formats.fullDateTime) : ''}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <ConfirmButton
                  text="Rotate"
                  bsStyle="warning"
                  hideNote
                  message="Rotating revokes this key and issues a new one. Any integration using the old key will stop working until updated."
                  okAction={() => rotateKey(key)}
                  style={{ marginRight: 6 }}
                />
                <ConfirmButton
                  text="Revoke"
                  bsStyle="danger"
                  hideNote
                  message="Revoking permanently disables this key. This cannot be undone."
                  okAction={() => revokeKey(key)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Create-key modal */}
      <StandardFormModal
        title="Create API Key"
        showModal={createModal.display}
        body={() => (
          <div>
            <div className="form-group">
              <label>Name</label>
              <input
                className="form-control"
                autoFocus
                placeholder="e.g. Claude"
                value={createModal.name}
                onChange={(e) => setCreateModal({ ...createModal, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') createKey(); }}
              />
              <small className="text-muted">A label to help you recognise this key later.</small>
            </div>
            <div className="form-group">
              <label>Access</label>
              <div className="radio">
                <label>
                  <input type="radio" name="scope" checked={createModal.scope === 'read_only'}
                    onChange={() => setCreateModal({ ...createModal, scope: 'read_only' })} />
                  {" "}Read only &mdash; safe for testing; blocks all writes
                </label>
              </div>
              <div className="radio">
                <label>
                  <input type="radio" name="scope" checked={createModal.scope === 'read_write'}
                    onChange={() => setCreateModal({ ...createModal, scope: 'read_write' })} />
                  {" "}Read &amp; write &mdash; full access (create/update deals, add notes, change stages)
                </label>
              </div>
            </div>
            <div className="text-right">
              <Button bsStyle="default" style={{ marginRight: 6 }} onClick={() => setCreateModal({ display: false, name: '', scope: 'read_only' })}>
                Cancel
              </Button>
              <Button bsStyle="success" onClick={createKey}>Create</Button>
            </div>
          </div>
        )}
        onCloseModal={() => setCreateModal({ display: false, name: '', scope: 'read_only' })}
      />

      {/* One-time token reveal modal */}
      <StandardFormModal
        title="Copy your API key"
        showModal={!!revealed}
        body={() => (
          <div>
            <div className="alert alert-warning">
              <i className="far fa-exclamation-triangle" /> Copy this key now &mdash; you won&rsquo;t be able to see it again.
            </div>
            <div className="form-group">
              <label>{revealed && revealed.name}</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="form-control" readOnly value={revealed ? revealed.token : ''} style={{ fontFamily: "monospace" }} />
                <Button bsStyle="info" onClick={() => copyToken(revealed.token)}>Copy</Button>
              </div>
            </div>
            <div className="text-right">
              <Button bsStyle="success" onClick={() => setRevealed(null)}>Done</Button>
            </div>
          </div>
        )}
        onCloseModal={() => setRevealed(null)}
      />
    </div>
  );
};

export default ApiKeys;
